import {useCallback, useEffect} from 'react';

import {useAppSelector} from 'app/store/hooks';
import {useNavigate} from 'react-router';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {selectUser} from '@/app/store/authSlice';
import {useAmplifyAuth} from '@/hooks/AmplifyAuth';
import logError from '@/utils/errors/logError';

const timeMilliseconds = 15 * 60 * 1000;

const InactivityMonitor = () => {
  const navigate = useNavigate();
  const {signOut} = useAmplifyAuth();
  const userData = useAppSelector(selectUser);
  const isLoggedIn = Boolean(userData);

  const goToAuth = useCallback((): void => {
    navigate('/authentication/sign-up');
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let timer: NodeJS.Timeout;

    const resetTimer = () => {
      if (timer) clearTimeout(timer);

      timer = setTimeout(async () => {
        try {
          // `signOut` (AmplifyService's `handleSignOut`) already unsets the
          // token/user in redux, clears storage, resets analytics, and
          // purges the persistor via `clearData` — don't duplicate that here.
          await signOut();
          goToAuth();
          showSnackbar({
            summary: `Session Expired`,
            message: `You've been logged out due to inactivity. Please log in again to continue`,
            variant: SnackbarType.INFO,
          });
        } catch (error) {
          logError(error, {
            fileName: 'MonitorActivity.tsx',
            functionName: 'useEffect',
          });
        }
      }, timeMilliseconds);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetTimer));

    resetTimer();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [isLoggedIn, signOut, goToAuth]);

  return null;
};

export default InactivityMonitor;
